import { Dialog } from '@base-ui/react/dialog';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx, clsx } from '@payaca/utilities/cvaMerge';
import { FC, PropsWithChildren } from 'react';
import Button from '../Button/Button';
import ModalCore, { ModalCoreProps } from './ModalCore';
import { useIsMdBreakpoint } from './useModalDrag';

export interface ModalProps extends ModalCoreProps {
  title: string;
  onBack?: () => void;
}

const Modal: FC<PropsWithChildren<ModalProps>> = (props) => {
  const {
    title,
    draggable = true,
    className,
    children,
    onBack,
    onClose,
    size = 'sm',
    ...rest
  } = props;

  const isMdAndUp = useIsMdBreakpoint();
  const showDragHandle = draggable && isMdAndUp;

  return (
    <ModalCore
      draggable={draggable}
      className={clsx(
        `flex flex-col overflow-hidden bg-white shadow-sm border-gray-200`,
        // Phone bottom sheet: only the top corners round off and only the top
        // edge is bordered — the other three sit flush against its container's
        // edges. The height cap comes from ModalCore's sheet layout.
        `max-sm:rounded-t-xl max-sm:border-t`,
        `sm:max-h-full sm:rounded-xl sm:border`,
        className
      )}
      onClose={onClose}
      size={size}
      {...rest}
    >
      {/*Header*/}
      <div
        data-modal-drag-handle
        className={clstx(
          'flex flex-col border-b border-gray-200 px-4 py-3',
          showDragHandle && 'cursor-move'
        )}
      >
        {showDragHandle && (
          <div className="mx-auto mb-1 h-1.5 w-8 rounded bg-gray-400/80">
            <span className="sr-only">Drag Modal</span>
          </div>
        )}

        <div className="typography:prose flex items-center gap-2">
          {onBack && (
            <Button variant="quaternary" size={'xs'} onClick={onBack}>
              <span className="sr-only">Back</span>
              <UntitledIcon className="h-5 w-5" name="chevron-left" />
            </Button>
          )}
          <Dialog.Title>{title}</Dialog.Title>
          {onClose && (
            <Button
              className="ml-auto"
              variant="quaternary"
              size={'sm'}
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <UntitledIcon className="h-3 w-3" name="x-close" />
            </Button>
          )}
        </div>
      </div>
      {/* `overscroll-y-contain`: the default `modal='trap-focus'` leaves
          background scroll unlocked, and as a phone sheet this covers nearly the
          whole viewport — without it, flicking past the end of a long body
          scrolls the page underneath. */}
      <div className="overflow-y-auto overscroll-y-contain">{children}</div>
    </ModalCore>
  );
};

const ModalBody: FC<PropsWithChildren<{ className?: string }>> = (props) => {
  const { className, children } = props;

  return (
    <div className={clstx('typography:prose p-4', className)}>{children}</div>
  );
};

const ModalFooter: FC<PropsWithChildren<{ className?: string }>> = (props) => {
  const { className, children } = props;

  return (
    <div
      className={clstx(
        'z-sticky sticky bottom-0 border-t border-gray-200 bg-white px-4 py-3',
        className
      )}
    >
      {children}
    </div>
  );
};

const ModalFooterActions: FC<PropsWithChildren<{ className?: string }>> = (
  props
) => {
  const { className, children } = props;

  return (
    <div className={clstx('flex items-center justify-end gap-x-2', className)}>
      {children}
    </div>
  );
};

export default Object.assign(Modal, {
  Body: ModalBody,
  Footer: Object.assign(ModalFooter, { Actions: ModalFooterActions }),
});
